import React, { useState, useRef } from 'react';
import { Send } from 'lucide-react';
import LoadingDots from './loading-dots';
import { ChatMessage } from 'src/redux/admin/reporting/reporting.types';

interface ChatInterfaceProps {
  isDisabled: boolean;
  onSendMessage: (message: string) => void;
  messages: ChatMessage[];
}

const PREDEFINED_MESSAGES = {
  "Generate Summary": "Generate a short execute summary of campaign performance.",
  "Generate Detailed Report": "Generate a detailed report analyzing all aspects of the campaign, including performance metrics & trends.",
  "Generate Email": "Write a professional email to update the client about the campaign's current performance and key metrics.",
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  isDisabled,
  onSendMessage,
  messages,
}) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isDisabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  return (
    <div className="flex-1">
      <div className="flex-1 overflow-y-auto mt-4 mb-2 lg:max-h-[530px] pr-4">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex mb-4 ${msg.type === 'human' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`py-2 px-4 max-w-[80%] text-gray-900 ${
                msg.type === 'human'
                  ? 'bg-gray-100 rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl'
                  : 'bg-gray-200 rounded-tr-2xl rounded-bl-2xl rounded-br-2xl'
              } ${msg.content === "..." ? 'flex items-center justify-center' : ''}`}
            >
              {msg.content === "..." ? <LoadingDots /> : msg.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="flex gap-2 mb-4 flex-wrap justify-center">
        {Object.entries(PREDEFINED_MESSAGES).map(([label, text]) => (
          <button
            key={label}
            onClick={() => onSendMessage(text)}
            disabled={isDisabled}
            className={`px-4 py-2 rounded-full border border-gray-400 text-sm shadow-sm ${
              isDisabled
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <form
        onSubmit={handleSubmit}
        className="relative"
      >
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={"Type your message..."}
          disabled={isDisabled}
          className={`w-full rounded-lg p-2 pr-10 border border-gray-400 focus:outline-none ${
            isDisabled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        />
        <button
          type="submit"
          disabled={isDisabled || !message.trim()}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md ${
            isDisabled || !message.trim() ? 'cursor-not-allowed' : 'hover:bg-gray-100'
          }`}
        >
          <Send className="size-5 text-blue-500" />
        </button>
      </form>
    </div>
  );
};

export default ChatInterface;